import styled from "styled-components";

import { Table } from "@similarweb/pro-logic-components";
import type { SubtitleFilter } from "@similarweb/pro-logic-components";
import { IconButton } from "@similarweb/ui-components";

import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { IndustryDataAvailabilityBanner } from "app/pages/digital-marketing/ad-intelligence/common/data-availability/IndustryDataAvailabilityBanner";
import { numberFilter } from "app/filters/numberFilter";
import { allTrackers } from "app/services/track/track";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";

import { INDUSTRY_CAMPAIGNS_DEFAULT_SERVER_SORT } from "../../../../advertisers/campaigns/IndustryCampaigns";
import { TOP_CAMPAIGNS_TABLE_ID } from "../../../../advertisers/overview/components/RecentCampaignsTable";
import { useSelectedIndustry } from "../../../../shared/components/AssetPicker/useSelectedIndustry";
import { useDisplayAdvertiserPublisherDataAndParams } from "../../../hooks/useDisplayAdvertiserPublisherDataAndParams";
import { useDurationSubtitleFilter } from "../../../hooks/useDurationSubtitleFilter";
import { WidgetBoxWithMarginBottom } from "../../../StyledComponents";
import { FIRST_PAGE, getNoDataObject, PAGE_SIZE_SMALL } from "../../../utils/utils";
import { TableFooter } from "../../StyledComponents";

import { getColumns } from "./IndustryTopCampaignsColumns";
import { useIndustryTopCampaigns } from "./useIndustryTopCampaigns";

// Parent Stack already provides the widget-to-widget gap.
const TopCampaignsWidgetBox = styled(WidgetBoxWithMarginBottom)`
    margin-bottom: 0;
`;

interface IIndustryTopCampaignsProps {
    category: IFlattenedCategory;
}

export const IndustryTopCampaigns = ({ category }: IIndustryTopCampaignsProps) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const { replaceWithDomain } = useSelectedIndustry();
    const { rows, totalCount, isLoading, isError, availability } = useIndustryTopCampaigns(
        category,
        FIRST_PAGE,
        PAGE_SIZE_SMALL,
        undefined,
        INDUSTRY_CAMPAIGNS_DEFAULT_SERVER_SORT,
        false,
    );

    const durationSubtitleFilter = useDurationSubtitleFilter();
    const subtitleFilters = useSubtitleFilters("country", "webSource");
    const combinedSubtitleFilters: SubtitleFilter[] = [durationSubtitleFilter, ...subtitleFilters];

    const handleSeeAll = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "Advertiser_overview/Top_campaigns/see_more");
        void displayAdvertisingNavigation.campaigns.go(params);
    };

    return (
        <TopCampaignsWidgetBox
            id={TOP_CAMPAIGNS_TABLE_ID}
            data-automation="industry-top-campaigns-widget"
        >
            <Table
                theme="white"
                bordersConfig={{ body: false, header: false }}
                columnConfig={getColumns(replaceWithDomain)}
                data={rows}
                isLoading={isLoading}
                loaderRowCount={PAGE_SIZE_SMALL}
                noData={getNoDataObject(isError)}
            >
                <Table.TableTop
                    borderBottom
                    withoutRowBorders
                >
                    <Table.TableTop.Title
                        withFiltersLoader
                        title={translate("ad.intelligence.industry.top.campaigns.table.title")}
                        tooltip={translate("ad.intelligence.overview.top.campaigns.table.title.tooltip")}
                        filters={combinedSubtitleFilters}
                    />
                    {/* The table owns its title row, so the availability notice goes in the
                        table-top slot to sit directly under the title. */}
                    {availability.isBeforeMinDate ? (
                        <Table.TableTop.Chart>
                            <IndustryDataAvailabilityBanner availability={availability} />
                        </Table.TableTop.Chart>
                    ) : null}
                </Table.TableTop>
            </Table>
            {totalCount > 0 ? (
                <TableFooter>
                    <IconButton
                        type="flat"
                        onClick={handleSeeAll}
                        iconName="arrow-right"
                        placement="right"
                        iconSize="xs"
                        data-automation="industry-top-campaigns-see-all"
                    >
                        {translate("ad.intelligence.overview.top.campaigns.table.see.all.button", {
                            totalCount: numberFilter()(totalCount),
                        })}
                    </IconButton>
                </TableFooter>
            ) : null}
        </TopCampaignsWidgetBox>
    );
};
